import { useState, useEffect } from 'react';
import { useConfirm } from '../components/ui';

/**
 * Estilos para react-select usando CSS variables del proyecto.
 * Se adaptan automaticamente a light/dark sin recomputar al cambiar de tema.
 *
 * Tokens utilizados (definidos en theme.css):
 *   --surface, --surface-2, --border, --text, --text-muted, --primary, --danger
 */
const v = (name, fallback) => `rgb(var(--${name})${fallback ? ` / ${fallback}` : ''})`;

export const selectStyles = {
    control: (base, state) => ({
        ...base,
        backgroundColor: v('surface'),
        // Borde: accent-cyan al focus, line/border normal. Mismo patron que
        // el primitivo <Input> (focus-within:border-cyan2 ring-cyan2/30) y
        // el DatePicker .cot-form-datepicker. Asi TODOS los campos comparten
        // el mismo "feel" de focus en toda la app.
        borderColor: state.isFocused ? v('accent-cyan') : v('border'),
        borderRadius: '8px',
        // Altura unificada con primitivo Input (h-10 = 40px) y DatePicker.
        minHeight: '40px',
        height: '40px',
        boxShadow: state.isFocused ? `0 0 0 2px ${v('accent-cyan', '0.3')}` : 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:hover': { borderColor: v('accent-cyan', '0.5') }
    }),
    menu: (base) => ({
        ...base,
        backgroundColor: v('surface'),
        border: `1px solid ${v('border')}`,
        borderRadius: '10px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.10), 0 12px 30px rgba(0,0,0,0.08)',
        zIndex: 100
    }),
    menuList: (base) => ({
        ...base,
        padding: '4px 0',
        '::-webkit-scrollbar': { width: '6px' },
        '::-webkit-scrollbar-track': { background: v('surface-2'), borderRadius: '3px' },
        '::-webkit-scrollbar-thumb': { background: v('border'), borderRadius: '3px' },
        '::-webkit-scrollbar-thumb:hover': { background: v('primary', '0.4') }
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? v('primary', '0.12')
            : state.isFocused
                ? v('surface-2')
                : 'transparent',
        color: state.isSelected ? v('primary') : v('text'),
        cursor: 'pointer',
        fontSize: '13px',
        padding: '8px 12px',
        '&:active': { backgroundColor: v('primary', '0.15') }
    }),
    singleValue: (base) => ({
        ...base,
        color: v('text'),
        fontSize: '14px'
    }),
    input: (base) => ({
        ...base,
        color: v('text'),
        fontSize: '14px'
    }),
    placeholder: (base) => ({
        ...base,
        color: v('text-muted'),
        fontSize: '14px'
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({
        ...base,
        color: v('text-muted'),
        padding: '6px',
        '&:hover': { color: v('text') }
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: v('primary', '0.12'),
        borderRadius: '4px',
        border: `1px solid ${v('primary', '0.3')}`
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: v('primary'),
        fontSize: '13px',
        padding: '2px 6px',
        fontWeight: 500
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: v('primary', '0.7'),
        cursor: 'pointer',
        '&:hover': { backgroundColor: v('danger', '0.2'), color: v('danger') }
    }),
    groupHeading: (base) => ({
        ...base,
        color: v('text-muted'),
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: '6px 12px'
    }),
    clearIndicator: (base) => ({
        ...base,
        color: v('text-muted'),
        padding: '6px',
        '&:hover': { color: v('danger') }
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 })
};

// Componente para input editable con guardado en blur
export const EditableInput = ({ value, onSave, type = 'text', placeholder = '', className = 'input', ...props }) => {
    const [localValue, setLocalValue] = useState(value ?? '');
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        setLocalValue(value ?? '');
    }, [value]);

    const handleChange = (e) => {
        setLocalValue(e.target.value);
        setIsDirty(true);
    };

    const handleBlur = () => {
        if (isDirty && localValue !== value) {
            onSave(type === 'number' ? parseFloat(localValue) || 0 : localValue);
            setIsDirty(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
    };

    return (
        <input
            className={className}
            type={type}
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            {...props}
        />
    );
};

// Componente para inputs secretos. Si `preview` viene (ej. "••••••••ABCD"
// enmascarado por el backend) se muestra tal cual — asi el user identifica
// que key esta guardada por sus ultimos caracteres. Sin `preview` cae al
// patron viejo de puntos genericos.
export const SecretInput = ({ hasValue, preview, onSave, onClear, placeholder = '' }) => {
    const confirm = useConfirm();
    const [isEditing, setIsEditing] = useState(false);
    const [newValue, setNewValue] = useState('');

    const handleSave = () => {
        if (newValue.trim()) {
            onSave(newValue.trim());
            setNewValue('');
            setIsEditing(false);
        }
    };

    const handleCancel = () => {
        setNewValue('');
        setIsEditing(false);
    };

    const handleClear = async () => {
        const ok = await confirm({
            title: '¿Eliminar valor configurado?',
            message: 'Vas a eliminar este valor. Tendrás que volver a configurarlo si lo necesitas.',
            confirmText: 'Eliminar',
            danger: true,
        });
        if (ok) onClear();
    };

    if (isEditing) {
        return (
            <div className="secret-input-edit">
                <input
                    className="input"
                    type="text"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    placeholder={placeholder}
                    autoFocus
                />
                <div className="secret-input-actions">
                    <button type="button" className="btn neon small" onClick={handleSave}>
                        Guardar
                    </button>
                    <button type="button" className="btn ghost small" onClick={handleCancel}>
                        Cancelar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="secret-input-display">
            {hasValue ? (
                <>
                    <span className="secret-value">
                        {preview || '••••••••••••••••••••••••'}
                    </span>
                    <div className="secret-input-actions">
                        <button type="button" className="btn ghost small" onClick={() => setIsEditing(true)}>
                            Cambiar
                        </button>
                        <button type="button" className="btn ghost small danger" onClick={handleClear}>
                            Eliminar
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <span className="secret-empty">Sin configurar</span>
                    <button type="button" className="btn neon small" onClick={() => setIsEditing(true)}>
                        Configurar
                    </button>
                </>
            )}
        </div>
    );
};
